import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, CheckCircle2, Send, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import AssignToClassesDialog from './AssignToClassesDialog';
import { useRemoveInstructor } from '~/pages/Courses/useRosterMutations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { readFragment } from 'gql.tada';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import type { AppLayoutContext } from '~/types';
import { useStaffMemberQuery } from './useStaffMemberQuery';
import { StaffMemberDetailFragment } from './StaffMemberFragment';
import {
  useUpdateStaffMember,
  useDeactivateStaffMember,
  useReactivateStaffMember,
  useDeleteStaffMember,
  useInviteStaffMember,
} from './useStaffMemberMutations';
import { useUpdatePersonMutation } from '~/pages/StudentDetail/useUpdatePersonMutation';

type StaffKind = 'TEACHER' | 'AIDE' | 'ADMIN' | 'OTHER';

const KIND_LABEL: Record<StaffKind, string> = {
  TEACHER: 'Teacher',
  AIDE: 'Aide',
  ADMIN: 'Admin',
  OTHER: 'Other',
};

export default function StaffMemberDetail() {
  const { school } = useOutletContext<AppLayoutContext>();
  const { staffMemberId } = useParams();
  const navigate = useNavigate();

  const { data, loading, refetch } = useStaffMemberQuery(school?.id, staffMemberId);
  const [updateStaffMember] = useUpdateStaffMember();
  const [deactivateStaffMember] = useDeactivateStaffMember();
  const [reactivateStaffMember] = useReactivateStaffMember();
  const [deleteStaffMember] = useDeleteStaffMember();
  const [inviteStaffMember] = useInviteStaffMember();
  const [updatePerson] = useUpdatePersonMutation();

  const sm = useMemo(
    () => (data?.school?.staffMember ? readFragment(StaffMemberDetailFragment, data.school.staffMember) : null),
    [data]
  );

  const [kind, setKind] = useState<StaffKind>('TEACHER');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [startedOn, setStartedOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<any>(null);
  const [removeInstructor] = useRemoveInstructor();

  useEffect(() => {
    if (sm) {
      setKind(sm.kind as StaffKind);
      setFirstName(sm.person.firstName);
      setLastName(sm.person.lastName);
      setEmail(sm.person.email || '');
      setTitle(sm.title || '');
      setEmployeeId(sm.employeeId || '');
      setStartedOn(String(sm.startedOn || ''));
      setError(null);
    }
  }, [sm]);

  const hasChanges =
    sm &&
    (sm.person.firstName !== firstName ||
      sm.person.lastName !== lastName ||
      (sm.person.email || '') !== email ||
      sm.kind !== kind ||
      (sm.title || '') !== title ||
      (sm.employeeId || '') !== employeeId ||
      (sm.startedOn || '') !== startedOn);

  const handleSave = useCallback(async () => {
    if (!sm) return;
    const normalizedFirstName = firstName.trim();
    const normalizedLastName = lastName.trim();
    const normalizedEmail = email.trim();
    if (!normalizedFirstName || !normalizedLastName) {
      setError('First and last name are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const personChanged =
        sm.person.firstName !== normalizedFirstName ||
        sm.person.lastName !== normalizedLastName ||
        (sm.person.email || '') !== normalizedEmail;
      const staffChanged =
        sm.kind !== kind ||
        (sm.title || '') !== title ||
        (sm.employeeId || '') !== employeeId ||
        (sm.startedOn || '') !== startedOn;

      if (personChanged) {
        const personResult = await updatePerson({
          variables: {
            input: {
              personId: sm.person.id,
              firstName: normalizedFirstName,
              lastName: normalizedLastName,
              email: normalizedEmail || null,
            },
          },
        });
        const personErrors = personResult.data?.updatePerson?.userErrors;
        if (personErrors?.length) {
          setError(personErrors.map((e: any) => e.message).join(', '));
          return;
        }
      }

      if (staffChanged) {
        const staffResult = await updateStaffMember({
          variables: {
            input: {
              id: sm.id,
              kind,
              title: title || null,
              employeeId: employeeId || null,
              startedOn: startedOn || null,
            },
          },
        });
        const staffErrors = staffResult.data?.updateStaffMember?.userErrors;
        if (staffErrors?.length) {
          setError(staffErrors.map((e: any) => e.message).join(', '));
          return;
        }
      }
      await refetch();
    } catch (e: any) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [sm, firstName, lastName, email, kind, title, employeeId, startedOn, updatePerson, updateStaffMember, refetch]);

  const handleDeactivate = useCallback(async () => {
    if (!sm) return;
    const futureCount = sm.currentAndFutureClassInstructorships?.length ?? 0;
    const msg =
      futureCount > 0
        ? `Deactivate ${sm.person.firstName} ${sm.person.lastName}?\n\nThey are currently assigned to ${futureCount} class${futureCount === 1 ? '' : 'es'}. Those assignments will stay in place — you can unassign them from each class page if needed.`
        : `Deactivate ${sm.person.firstName} ${sm.person.lastName}?`;
    if (!window.confirm(msg)) return;
    await deactivateStaffMember({ variables: { input: { id: sm.id } } });
    await refetch();
  }, [sm, deactivateStaffMember, refetch]);

  const handleReactivate = useCallback(async () => {
    if (!sm) return;
    await reactivateStaffMember({ variables: { input: { id: sm.id } } });
    await refetch();
  }, [sm, reactivateStaffMember, refetch]);

  const handleInvite = useCallback(async () => {
    if (!sm) return;
    if (!sm.person.email) {
      window.alert('Add an email to this person before inviting them to log in.');
      return;
    }
    const isReinvite = sm.loginState === 'INVITED';
    const msg = isReinvite
      ? `Resend invitation email to ${sm.person.email}? Any prior link will become invalid.`
      : `Send an invitation to ${sm.person.email}? They'll receive an email with a link to set their password.`;
    if (!window.confirm(msg)) return;
    const res = await inviteStaffMember({ variables: { input: { staffMemberId: sm.id } } });
    const userErrors = res.data?.inviteStaffMember?.userErrors;
    if (userErrors?.length) {
      window.alert(userErrors.map((e: any) => e.message).join(', '));
      return;
    }
    const sentEmail = res.data?.inviteStaffMember?.sentEmail;
    if (sentEmail) {
      window.alert('Invitation email sent.');
    } else {
      window.alert('This person already has an account. Linked successfully.');
    }
    await refetch();
  }, [sm, inviteStaffMember, refetch]);

  const handleDelete = useCallback(async () => {
    if (!sm) return;
    if (!window.confirm(`Permanently delete ${sm.person.firstName} ${sm.person.lastName}'s staff record? This cannot be undone.`)) return;
    const result = await deleteStaffMember({ variables: { input: { id: sm.id } } });
    const userErrors = result.data?.deleteStaffMember?.userErrors;
    if (userErrors?.length) {
      window.alert(userErrors.map((e: any) => e.message).join(', '));
      return;
    }
    navigate(`/app/${school?.slug}/staff`);
  }, [sm, deleteStaffMember, navigate, school?.slug]);

  if (loading && !sm) {
    return <div className="p-6"><p>Loading…</p></div>;
  }

  if (!sm) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(`/app/${school?.slug}/staff`)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Staff
        </Button>
        <Card><CardHeader><CardTitle>Staff member not found</CardTitle></CardHeader></Card>
      </div>
    );
  }

  const hasAnyAssignments = (sm.classInstructorships?.length ?? 0) > 0;
  const isActive = sm.state === 'ACTIVE';

  return (
    <div className="p-6 space-y-4">
      <Button variant="ghost" onClick={() => navigate(`/app/${school?.slug}/staff`)}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Staff
      </Button>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">
            {sm.person.lastName}, {sm.person.firstName}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline">{KIND_LABEL[sm.kind as StaffKind]}</Badge>
            {isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive (since {String(sm.endedOn ?? '')})</Badge>}
            {sm.person.email && <span className="text-sm text-muted-foreground">{sm.person.email}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {isActive ? (
            <Button variant="outline" onClick={handleDeactivate}>Deactivate</Button>
          ) : (
            <Button variant="outline" onClick={handleReactivate}>Reactivate</Button>
          )}
          <Button
            variant="ghost"
            onClick={handleDelete}
            disabled={hasAnyAssignments}
            title={hasAnyAssignments ? 'Cannot delete: has class assignments. Deactivate instead.' : undefined}
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="email">Email (optional)</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sm.loginState !== 'NONE'}
              />
              {sm.loginState !== 'NONE' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  The login email cannot be changed after an account has been linked.
                </p>
              )}
            </div>
            <div>
              <Label>Kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as StaffKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TEACHER">Teacher</SelectItem>
                  <SelectItem value="AIDE">Aide</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="startedOn">Started on</Label>
              <Input id="startedOn" type="date" value={startedOn} onChange={(e) => setStartedOn(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Math Dept Chair" />
            </div>
            <div>
              <Label htmlFor="employeeId">Employee ID</Label>
              <Input id="employeeId" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {hasChanges && (
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="ghost" onClick={() => refetch()}>Discard</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Login Access</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              {sm.loginState === 'ACTIVE' && (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>Has an active account ({sm.person.email})</span>
                </>
              )}
              {sm.loginState === 'INVITED' && (
                <>
                  <Mail className="h-4 w-4 text-amber-600" />
                  <span>Invitation pending — they haven't set their password yet</span>
                </>
              )}
              {sm.loginState === 'NONE' && (
                <span className="text-muted-foreground">
                  {sm.person.email
                    ? 'No login account yet.'
                    : 'Add an email to this person before inviting them to log in.'}
                </span>
              )}
            </div>
            {(sm.loginState === 'NONE' || sm.loginState === 'INVITED') && sm.person.email && (
              <Button variant="outline" size="sm" onClick={handleInvite}>
                <Send className="h-3.5 w-3.5 mr-1" />
                {sm.loginState === 'INVITED' ? 'Resend invite' : 'Invite to log in'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Class Assignments</CardTitle>
            {isActive && (
              <Button size="sm" onClick={() => setAssignOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Assign to classes
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {hasAnyAssignments ? (
            <ul className="space-y-1 text-sm">
              {sm.classInstructorships.map((ci: any) => {
                const co = ci.classOffering;
                const yearSlug = co?.course?.schoolYear?.slug;
                const courseSlug = co?.course?.slug;
                const classLink = school?.slug && yearSlug && courseSlug && co?.id
                  ? `/app/${school.slug}/years/${yearSlug}/courses/${courseSlug}/classes/${co.id}`
                  : null;
                return (
                  <li key={ci.id} className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-muted/50">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Badge variant="outline">{ci.role}</Badge>
                      {classLink ? (
                        <Link to={classLink} className="hover:underline truncate">
                          {co?.course?.name} — {co?.displayName || `Section ${co?.section}`}
                        </Link>
                      ) : (
                        <span className="truncate">
                          {co?.course?.name} — {co?.displayName || `Section ${co?.section}`}
                        </span>
                      )}
                      {co?.term?.name && (
                        <Badge variant="secondary" className="text-xs">{co.term.name}</Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setRemoveConfirm(ci)}
                      title="Remove from this class"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              No class assignments yet. Click "Assign to classes" above to pick sections.
            </p>
          )}
        </CardContent>
      </Card>

      {isActive && school && (
        <AssignToClassesDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          staffPersonId={sm.person.id}
          staffDisplayName={`${sm.person.firstName} ${sm.person.lastName}`}
          schoolYears={school.schoolYears ?? []}
          alreadyAssigned={(sm.classInstructorships ?? []).map((ci: any) => ({
            classOfferingId: ci.classOffering?.id,
            role: ci.role,
          }))}
          onComplete={() => {
            setAssignOpen(false);
            refetch();
          }}
        />
      )}

      <AlertDialog open={!!removeConfirm} onOpenChange={() => setRemoveConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from class</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {sm.person.firstName} {sm.person.lastName} as instructor of {removeConfirm?.classOffering?.course?.name} — {removeConfirm?.classOffering?.displayName}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!removeConfirm) return;
                await removeInstructor({
                  variables: {
                    input: {
                      classOfferingId: removeConfirm.classOffering.id,
                      personId: sm.person.id,
                    },
                  },
                });
                setRemoveConfirm(null);
                await refetch();
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
